import { apiGet, apiPut } from './index'
import { isListenerCountDebugEnabled, withListenerDebugAsync } from '../utils/listenerDebug'

export interface StationGeneralSettingsResponse {
  station_id: number
  show_listener_counts: boolean
  listener_count_endpoint: string
  auto_send_quick_replies: boolean
  enter_to_send: boolean
}

export interface ListenerCountResponse {
  enabled: boolean
  count: number | null
  last_updated: string | null
  stale: boolean | null
  raw_text?: string | null
  http_status?: number | null
  cache?: string | null
}

export type ListenerCountHistoryPeriod = '1h' | '6h' | '24h' | '3d' | '7d' | '30d' | '90d'

export interface ListenerCountHistoryPoint {
  recorded_at: string
  count: number | null
  source_url?: string
}

export interface ListenerCountHistoryResponse {
  station_id: number
  period: ListenerCountHistoryPeriod
  last_refresh: string | null
  points: ListenerCountHistoryPoint[]
}

export async function fetchStationGeneralSettings(
  stationId: number,
): Promise<StationGeneralSettingsResponse> {
  const params = new URLSearchParams({ station_id: String(stationId) })

  return apiGet<StationGeneralSettingsResponse>(`station/settings/general?${params.toString()}`)
}

export async function updateStationGeneralSettings(
  stationId: number,
  payload: {
    show_listener_counts: boolean
    listener_count_endpoint: string
    auto_send_quick_replies: boolean
    enter_to_send: boolean
  },
): Promise<StationGeneralSettingsResponse> {
  const params = new URLSearchParams({ station_id: String(stationId) })

  return apiPut<StationGeneralSettingsResponse>(
    `station/settings/general?${params.toString()}`,
    payload,
  )
}

export async function getListenerCount(
  stationId: number,
  options?: { force?: boolean; debug?: boolean },
): Promise<ListenerCountResponse> {
  const params = new URLSearchParams({ station_id: String(stationId) })

  if (options?.force) {
    params.set('force', '1')
  }

  const debugEnabled = options?.debug ?? isListenerCountDebugEnabled()

  if (debugEnabled) {
    params.set('debug', '1')
  }

  const requestPath = `station/listener-count?${params.toString()}`

  return withListenerDebugAsync(
    `api:getListenerCount station=${stationId} force=${options?.force ? '1' : '0'}`,
    async (log) => {
      log.info('Requesting listener count', {
        stationId,
        force: Boolean(options?.force),
        debug: debugEnabled,
        path: requestPath,
      })

      try {
        const response = await apiGet<unknown>(requestPath)
        log.info('Raw response payload', response)
        const normalized = normalizeListenerCountResponse(response)
        log.info('Normalized response', {
          enabled: normalized.enabled,
          count: normalized.count,
          last_updated: normalized.last_updated,
          stale: normalized.stale,
          cache: normalized.cache ?? null,
          http_status: normalized.http_status ?? null,
        })
        return normalized
      } catch (error) {
        log.error('Listener count request failed', error)

        if (isListenerCountDisabledError(error)) {
          const fallback: ListenerCountResponse = {
            enabled: false,
            count: null,
            last_updated: null,
            stale: null,
            raw_text: null,
            http_status: null,
            cache: null,
          }
          log.warn('Listener count disabled; returning fallback payload', fallback)
          return fallback
        }

        throw error
      }
    },
  )
}

export async function getListenerCountHistory(
  stationId: number,
  period?: ListenerCountHistoryPeriod,
): Promise<ListenerCountHistoryResponse> {
  const params = new URLSearchParams({ station_id: String(stationId) })

  if (period) {
    params.set('period', period)
  }

  return apiGet<ListenerCountHistoryResponse>(
    `station/listener-count-history?${params.toString()}`,
  )
}

function isListenerCountDisabledError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false
  }

  const status = (error as { status?: number }).status

  if (status === 404) {
    return true
  }

  const data = (error as { data?: unknown }).data

  if (data && typeof data === 'object' && 'code' in data) {
    const code = String((data as { code?: unknown }).code ?? '')
    if (code === 'rpa_listener_count_disabled' || code === 'rpa_invalid_listener_endpoint') {
      return true
    }
  }

  return false
}

function normalizeListenerCountResponse(payload: unknown): ListenerCountResponse {
  const base: ListenerCountResponse = {
    enabled: false,
    count: null,
    last_updated: null,
    stale: null,
    raw_text: null,
    http_status: null,
    cache: null,
  }

  if (!payload || typeof payload !== 'object') {
    return base
  }

  const data = payload as Record<string, unknown>

  const enabled = typeof data.enabled === 'boolean' ? data.enabled : base.enabled
  const count = coerceNullableNumber(data.count)
  const lastUpdated = typeof data.last_updated === 'string' ? data.last_updated : base.last_updated
  const stale = typeof data.stale === 'boolean' ? data.stale : base.stale

  const rawText =
    typeof data.raw_text === 'string'
      ? data.raw_text
      : data.raw_text === null
        ? null
        : base.raw_text
  const httpStatus =
    typeof data.http_status === 'number' && Number.isFinite(data.http_status)
      ? Math.trunc(data.http_status)
      : data.http_status === null
        ? null
        : base.http_status
  const cache = typeof data.cache === 'string' ? data.cache : base.cache

  return {
    enabled,
    count,
    last_updated: lastUpdated,
    stale,
    raw_text: rawText,
    http_status: httpStatus,
    cache,
  }
}

function coerceNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value === 'number') {
    if (Number.isFinite(value)) {
      return Math.trunc(value)
    }
    return null
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()

    if (trimmed === '') {
      return null
    }

    const parsed = Number.parseInt(trimmed, 10)

    if (!Number.isNaN(parsed)) {
      return parsed
    }
  }

  return null
}
